import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  checkSocketConnectionAsync,
  errors,
  getSocket,
  IRydeClientEvents,
  IRydeEvent,
  isLoggedInAtom,
  isSocketEventsConnected,
  Log,
  nowTDateISO,
  RYDE_EVENTS_LIMIT,
  rydeEvents,
  TimeZoneEnum,
  useTimezone,
} from "@ryde/shared";

const resolveRydeEvents = (
  event: IRydeEvent,
  timezone: TimeZoneEnum,
): ((currentRydeEvents: IRydeClientEvents[]) => IRydeClientEvents[]) => {
  return (currentRydeEvents: IRydeClientEvents[]) => {
    const newRydeEvents = currentRydeEvents.slice(0, RYDE_EVENTS_LIMIT);
    return [
      ...newRydeEvents,
      { ...event, arrivedTimestamp: nowTDateISO(timezone) },
    ];
  };
};

export const RydeEvents = (): null => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const isLoggedIn = useRecoilValue(isLoggedInAtom);
  const setIsConnected = useSetRecoilState(isSocketEventsConnected);
  const setRydeEvents = useSetRecoilState(rydeEvents);

  useEffect(() => {
    const initializeSocket = async (): Promise<void> => {
      try {
        const isSocketConnected = await checkSocketConnectionAsync(undefined, {
          allowCookieAuth: isLoggedIn,
        });

        if (!isSocketConnected) {
          Log.warnAndToast(t(errors.common.socketNotConnected));
        }
      } catch (error) {
        Log.warnAndToast(t(errors.common.socketNotConnected), error);
      }
    };

    initializeSocket();

    return () => {
      const socket = getSocket();

      if (socket?.connected) {
        Log.debug("Websocket disconnect on component unmount");
        socket.disconnect();
      }
    };
  }, [isLoggedIn, t]);

  useEffect(() => {
    const socket = getSocket();

    if (!socket) {
      Log.warn("Socket not available, skipping event listeners setup");
      return;
    }

    const onConnect = (): void => {
      Log.log(t("login.WS_CONNECTED"));
      setIsConnected(true);
    };

    const onDisconnect = (reason: string): void => {
      Log.log(`Websocket disconnected: ${reason}`);
      setIsConnected(false);

      if (reason !== "io client disconnect") {
        checkSocketConnectionAsync(undefined, {
          allowCookieAuth: isLoggedIn,
        }).catch(error => {
          Log.error("Automatic reconnect failed", error);
        });
      }
    };

    const onEvent = (value: IRydeEvent): void => {
      Log.debug("Ryde event received", value);
      setRydeEvents(resolveRydeEvents(value, timezone));
    };

    const onException = (message: unknown): void => {
      Log.warn("Socket exception", message);
      Log.errorAndToast(t(errors.common.technicalProblemOccurred));
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("event", onEvent);
    socket.on("exception", onException);

    if (socket.connected) {
      onConnect();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("event", onEvent);
      socket.off("exception", onException);
    };
  }, [isLoggedIn, setIsConnected, setRydeEvents, t, timezone]);

  return null;
};
